/*
 * Copyright (c) 2020. American Express Company
 */

// third party
import _ from 'lodash';
import { IconCards, Label, SelectOption } from '@americanexpress/dls-react';
import React, { useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

// models and services
import { InvestmentDrawerViewModel } from 'src/types/view-models/investment-drawer.view.model';
import { InvestmentDrawerFormValues } from 'src/types/form-models/investment-drawer-form-values.model';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import InvestmentHoldingService from '../../services/investment-holding.service';
import LiteralsService from '../../services/literals/LiteralsService';
import NavigationService from 'src/services/navigation.service';

// components
import BasicInput from 'src/components/formik-fields/basic-input/BasicInput';
import Anchor from '../atomic/atoms/anchor/Anchor';
import CurrencyInput from '../formik-fields/currency-input/CurrencyInput';
import Drawer from 'src/components/drawer/Drawer';
import Dropdown from '../formik-fields/dropdown/Dropdown';
import FormatService from '../../services/format.service';
import InfoCallout from '../callout/InfoCallout';
import OptionsBox from 'src/components/options-box/OptionsBox';
import Output from 'src/components/atomic/molecules/output/Output';
import PercentInput from '../formik-fields/percent-input/PercentInput';
import PercentSlider from 'src/components/formik-fields/percent-slider/PercentSlider';
import TickerInput from 'src/components/formik-fields/ticker-input/TickerInput';
import ConnectAccountBottomButtons from 'src/components/connect-account-bottom-buttons/ConnectAccountBottomButtons';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// icons and styles
import { CUSTOM_ASSETS } from '../../config';
import styles from './InvestmentDrawer.module.scss';
import Selectors from 'src/utils/selectors';

export default function InvestmentDrawer({
    currentInvestment,
    typeFromURL,
    history,
    handleOpenConfirmation,
    handleBack,
    handleTopDrawer,
    onStartupStep,
    handleReloadUser,
    firstImport,
    handleAccounts,
    handleClearFirstImport,
    handleFirstImport,
    setDrawer,
    user
}: InvestmentDrawerViewModel): JSX.Element {
    const [mortgageRedirect, setMortgageRedirect] = React.useState(false);

    const goBack = async (): Promise<void> => {
        if (onStartupStep) {
            handleTopDrawer();
        } else {
            await handleAccounts();
        }
    };

    const handleUpsert = _.once(async (values: InvestmentDrawerFormValues): Promise<void> => {
        const formattedInterest: number = values?.interest / 100;

        const updatedValues = {
            ...values,
            interest: formattedInterest
        };

        const investment: InvestmentHoldingModel = {
            ...currentInvestment,
            ...updatedValues
        };

        if (investment.id) {
            await InvestmentHoldingService.updateInvestmentHolding(investment);
        } else {
            if (investment.type === 'real_estate' && !_.get(investment, 'attributes.address')) {
                investment.attributes = investment.attributes || {};
                investment.attributes.address = values.name;
            }
            await InvestmentHoldingService.postInvestmentHolding(investment);
        }

        await handleReloadUser();
        if (mortgageRedirect) await NavigationService.goTo(history, '/debt/mortgage', 'Add Debt');
        else await goBack();
    });

    const handleDelete = _.once(async (): Promise<void> => {
        await InvestmentHoldingService.deleteInvestmentHolding(currentInvestment.id);
        await handleReloadUser();
        await goBack();
    });

    const TypeSpecificInputs = (): JSX.Element => {
        const { values, setFieldValue, submitForm, resetForm, initialValues, isSubmitting } =
            useFormikContext<InvestmentDrawerFormValues>();

        // reset form when investment type changes
        useEffect(() => {
            if (!isSubmitting) {
                resetForm({
                    values: {
                        ...initialValues,
                        type: values.type
                    }
                });
            }
        }, [values.type]);

        // update amount when price and quantity change, for types with price and quantity
        useEffect(() => {
            if (['stock', 'mutual_fund', 'private_stock'].includes(values.type)) {
                let newAmount: number = 0;
                if (values.price > 0 && values.quantity > 0) {
                    newAmount = values.price * values.quantity;
                }
                setFieldValue('amount', newAmount);
            }
        }, [values.price, values.quantity]);

        switch (values.type) {
            case 'cash':
                return (
                    <PercentInput
                        id="interest"
                        label={LiteralsService.getLiteral('InvestmentDrawer.Cash.interestLabel')}
                    />
                );
            case 'stock':
            case 'mutual_fund':
            case 'private_stock':
                return (
                    <>
                        {values.type !== 'private_stock' && (
                            <TickerInput
                                id="ticker"
                                label={LiteralsService.getLiteral('InvestmentDrawer.Stock.tickerLabel')}
                            />
                        )}
                        <div className="drawerSection">
                            <CurrencyInput
                                id="price"
                                showFractions
                                label={LiteralsService.getLiteral('InvestmentDrawer.Stock.priceLabel')}
                                hint={LiteralsService.getLiteral('InvestmentDrawer.Stock.priceHint')}
                            />
                            {values.type !== 'private_stock' && values.price > 0 && (
                                <InfoCallout
                                    noMargin
                                    text={LiteralsService.getLiteral('InvestmentDrawer.priceLastUpdated')}
                                />
                            )}
                        </div>
                        <BasicInput
                            id="quantity"
                            label={LiteralsService.getLiteral('InvestmentDrawer.Stock.quantityLabel')}
                        />
                        <Output
                            title={LiteralsService.getLiteral('InvestmentDrawer.Stock.totalTitle')}
                            main={FormatService.formatString(values.amount || 0)}
                        />
                    </>
                );
            case 'real_estate':
                return (
                    <>
                        <div>
                            <Label>{LiteralsService.getLiteral('InvestmentDrawer.RealEstate.mortgageLabel')}</Label>
                            <Anchor
                                onClick={async () => {
                                    setMortgageRedirect(true);
                                    await submitForm();
                                }}
                                typographyProps={{
                                    variant: TypographyVariants.BODY,
                                    component: 'div',
                                    className: styles.mortgageLink
                                }}
                            >
                                {LiteralsService.getLiteral('InvestmentDrawer.RealEstate.mortgageLink')}
                            </Anchor>
                        </div>
                    </>
                );
            case 'custom':
                return (
                    <>
                        <PercentSlider
                            id="interest"
                            label={LiteralsService.getLiteral('InvestmentDrawer.Custom.interestLabel')}
                            min={-25}
                            max={25}
                        />
                        <InfoCallout
                            text={LiteralsService.getLiteral('InvestmentDrawer.Custom.growthCallout')}
                            noMargin
                        />
                    </>
                );
            default:
                return <></>;
        }
    };

    const currentInvestmentTypeDisplayName = (investmentType: string): string => {
        let displayName: string;
        if (currentInvestment) {
            const types: string[][] = _.filter(
                InvestmentHoldingModel.investmentTypes,
                (pair: string[]) => pair[1] === investmentType
            );
            if (types.length) {
                displayName = types[0][0];
            }
        }
        // as a last resort, display the investment type key (this should never happen)
        return displayName || investmentType;
    };

    const InvestmentTypeHeader = (): JSX.Element => {
        const { values } = useFormikContext<InvestmentDrawerFormValues>();
        return (
            <div>
                <Label>{LiteralsService.getLiteral('InvestmentDrawer.assetTypeLabel')}</Label>
                <Typography variant={TypographyVariants.HEADING_M}>
                    {currentInvestmentTypeDisplayName(values.type)}
                </Typography>
            </div>
        );
    };

    const activeInvestmentTypes = (): string[][] => {
        // don't put crypto in this list, since it has its own interface
        return InvestmentHoldingModel.investmentTypes.filter((it: string[]) => {
            if (CUSTOM_ASSETS) {
                return it[1] !== 'crypto';
            }
            return it[1] !== 'crypto' && it[1] !== 'custom';
        });
    };

    const amountLabel = (type: string): string | null => {
        switch (type) {
            case 'cash':
                return LiteralsService.getLiteral('InvestmentDrawer.Cash.amountLabel');
            case 'real_estate':
            case 'collectables':
            case 'custom':
            case 'other':
                return LiteralsService.getLiteral('InvestmentDrawer.valueLabel');
            case 'retire':
            case 'college':
            case 'bonds':
                return LiteralsService.getLiteral('InvestmentDrawer.balanceLabel');
            default:
                return null;
        }
    };

    return (
        <Drawer
            drawerProps={{ history }}
            withBackButton
            handleBack={handleBack}
            title={LiteralsService.getLiteral(
                currentInvestment ? 'InvestmentDrawer.updateTitle' : 'InvestmentDrawer.addTitle'
            )}
            data-cy={Selectors.PlanAhead.INVESTMENT_DRAWER}
            className={styles.investmentDrawer}
        >
            <OptionsBox
                headerText={LiteralsService.getLiteral('ConnectAssetAccounts.header')}
                icon={<IconCards />}
                content={[
                    LiteralsService.getLiteral('ConnectAssetAccounts.content1'),
                    LiteralsService.getLiteral('ConnectAssetAccounts.content2', [LiteralsService.getAppName()])
                ]}
                isBlue
                hasPlaidButton={true}
                firstImport={firstImport}
                handleAccounts={handleAccounts}
                handleClearFirstImport={handleClearFirstImport}
                handleFirstImport={handleFirstImport}
                setDrawer={setDrawer}
                user={user}
            />

            <Formik
                initialValues={{
                    type: currentInvestment?.type || typeFromURL || '',
                    name: currentInvestment?.name || '',
                    amount: currentInvestment?.amount,
                    price: currentInvestment?.price,
                    quantity: currentInvestment?.quantity,
                    ticker: currentInvestment?.ticker || '',
                    interest: Math.round(currentInvestment?.interest * 100) || 0
                }}
                validationSchema={Yup.object({
                    type: Yup.string()
                        .required(LiteralsService.getLiteral('InvestmentDrawer.assetTypeError'))
                        .oneOf(
                            InvestmentHoldingModel.investmentTypeKeys,
                            LiteralsService.getLiteral('InvestmentDrawer.assetTypeError')
                        ),
                    name: Yup.string().required(LiteralsService.getLiteral('InvestmentDrawer.nameInputError')),
                    amount: Yup.number()
                        .moreThan(0, LiteralsService.getLiteral('InvestmentDrawer.amountMustBeGreaterThanZeroError'))
                        .required(LiteralsService.getLiteral('InvestmentDrawer.amountInputError')),
                    price: Yup.number().when('type', {
                        is: (type: string) => type === 'stock' || type === 'mutual_fund' || type === 'private_stock',
                        then: Yup.number()
                            .moreThan(
                                0,
                                LiteralsService.getLiteral('InvestmentDrawer.Stock.priceMustBeGreaterThanZeroError')
                            )
                            .required(LiteralsService.getLiteral('InvestmentDrawer.Stock.priceError'))
                    }),
                    quantity: Yup.number().when('type', {
                        is: (type: string) => type === 'stock' || type === 'mutual_fund' || type === 'private_stock',
                        then: Yup.number()
                            .moreThan(
                                0,
                                LiteralsService.getLiteral('InvestmentDrawer.Stock.quantityMustBeGreaterThanZeroError')
                            )
                            .required(LiteralsService.getLiteral('InvestmentDrawer.Stock.quantityError'))
                    }),
                    ticker: Yup.string().when('type', {
                        is: (type: string) => type === 'stock' || type === 'mutual_fund',
                        then: Yup.string().required(LiteralsService.getLiteral('InvestmentDrawer.Stock.tickerError'))
                    }),
                    interest: Yup.number()
                })}
                onSubmit={handleUpsert}
            >
                {({ values }) => (
                    <Form className={styles.form}>
                        {currentInvestment ? (
                            <InvestmentTypeHeader />
                        ) : (
                            <Dropdown id="type" label={LiteralsService.getLiteral('InvestmentDrawer.assetTypeLabel')}>
                                {activeInvestmentTypes().map((type: string[]) => {
                                    return (
                                        <SelectOption key={type[1]} value={type[1]}>
                                            {type[0]}
                                        </SelectOption>
                                    );
                                })}
                            </Dropdown>
                        )}

                        {values.type && (
                            <BasicInput
                                label={LiteralsService.getLiteral('InvestmentDrawer.nameInputLabel')}
                                id="name"
                            />
                        )}

                        {amountLabel(values.type) && (
                            <CurrencyInput
                                id="amount"
                                label={amountLabel(values.type)}
                                hint={
                                    values.type === 'real_estate'
                                        ? LiteralsService.getLiteral('InvestmentDrawer.RealEstate.amountHint')
                                        : null
                                }
                            />
                        )}

                        <TypeSpecificInputs />

                        <ConnectAccountBottomButtons
                            currentAccount={currentInvestment}
                            type={'asset'}
                            handleBack={handleBack}
                            handleOpenConfirmation={handleOpenConfirmation}
                            handleDelete={handleDelete}
                        />
                    </Form>
                )}
            </Formik>
        </Drawer>
    );
}
